import CONST from './CONST';

const isSelected = (
    { selections },
    promptId,
    optionId
) => {
    const promptSelections = selections[promptId] || [];
    if (optionId) return promptSelections.includes(optionId);
    return promptSelections.length > 0;
}

const isSkipped = (
    { history },
    pageId,
) => history.find(
    ({ id, skipped }) => id === pageId && skipped
) !== undefined;

const program = {
    version: 1,

    /**
     * Enables features exclusively for development
     */
    debug: true && window.location.host.indexOf('local') !== -1,

    /**
     * Saves state to local storage
     */
    useLocalStorage: false,

    prompts: [
        {
            id: CONST.PROMPT_OS,
            control: CONST.CONTROL_TOGGLE,
            title: 'Operating System',
            limit: {
                max: 1,
                min: 1,
            },
            defaults: [CONST.OPTION_OS_WINDOWS],
            options: [
                {
                    value: CONST.OPTION_OS_WINDOWS,
                    icon: 'fab fa-windows',
                    text: 'Windows',
                },
                {
                    value: CONST.OPTION_OS_MAC,
                    icon: 'fab fa-apple',
                    text: 'Mac',
                },
            ],
        },
        {
            id: CONST.PROMPT_FINANCES,
            control: CONST.CONTROL_SELECTOR,
            title: 'Find the right plan for you.',
            limit: {
                max: 1,
                min: 1,
            },
            subtitle: 'My finances',
            options: [
                {
                    value: CONST.OPTION_FINANCES_STRAIGHTFORWARD,
                    text: 'are straightforward',
                },
                {
                    value: CONST.OPTION_FINANCES_COMPLEX,
                    text: 'are more complex',
                },
                {
                    value: CONST.OPTION_FINANCES_BIZ_RENTAL,
                    text: 'include small business and/or rental properties',
                    matcher: (state) => isSelected(
                        state,
                        CONST.PROMPT_OS,
                        CONST.OPTION_OS_WINDOWS
                    ),
                },
            ],
        },
        {
            id: CONST.PROMPT_GOALS,
            control: CONST.CONTROL_SELECTOR,
            title: 'What are your goals?',
            subtitle: 'Select up to your top three...',
            limit: {
                max: 3,
                min: 1,
            },
            options: [
                {
                    value: CONST.OPTION_GOALS_BILLS,
                    text: 'Track & pay my bills',
                },
                {
                    value: CONST.OPTION_GOALS_UNDERSTANDING,
                    text: 'Manage my spending',
                },
                {
                    value: CONST.OPTION_GOALS_BUDGET,
                    text: 'Stay on budget',
                },
                {
                    value: CONST.OPTION_GOALS_SAVE,
                    text: 'Save more money',
                },
                {
                    value: CONST.OPTION_GOALS_TAXES,
                    text: 'Prepare for taxes',
                },
                {
                    value: CONST.OPTION_GOALS_DEBT,
                    text: 'Pay down debt',
                },
                {
                    value: CONST.OPTION_GOALS_RETIREMENT,
                    text: 'Plan for retirement',
                },
                {
                    value: CONST.OPTION_GOALS_INVESTMENTS,
                    text: 'Monitor & grow investments',
                },
                {
                    value: CONST.OPTION_GOALS_OTHER,
                    text: 'Other',
                },
            ],
        },
        {
            id: CONST.PROMPT_ACCESS,
            control: CONST.CONTROL_SELECTOR,
            title: 'My primary access to financial software will be:',
            subtitle: 'Select one...',
            limit: {
                max: 1,
                min: 1,
            },
            options: [
                {
                    value: CONST.OPTION_ACCESS_OFFLINE,
                    text: 'Download, install, & run on my computer',
                },
                {
                    value: CONST.OPTION_ACCESS_WEB,
                    text: 'Use a web browser',
                },
                {
                    value: CONST.OPTION_ACCESS_MOBILE,
                    text: 'Use a mobile app',
                },
            ],
        },
        {
            id: CONST.PROMPT_FEATURES,
            control: CONST.CONTROL_SELECTOR,
            title: 'Must-have features.',
            subtitle: 'Select one or more...',
            limit: {
                max: Infinity,
                min: 1,
            },
            options: [
                {
                    value: CONST.OPTION_FEATURES_BILL_PAY,
                    text: 'Pay bills',
                },
                {
                    value: CONST.OPTION_FEATURES_INVESTMENTS,
                    text: 'Advanced investments',
                },
                {
                    value: CONST.OPTION_FEATURES_TAX_REPORTS,
                    text: 'Automatic tax reports',
                },
            ],
        },
    ],

    /**
     * Configuration of each page in the flow
     */
    pages: [
        {
            id: CONST.PAGE_FINANCES,

            // Can this page be skipped by the user?
            skippable: false,

            // Prompts which should appear on this page.
            prompts: [
                CONST.PROMPT_OS,
                CONST.PROMPT_FINANCES
            ],

            // Receives state, and uses it to determine which page
            // should be displayed when the user clicks "next."
            // If this callback is not present or fails to return
            // a page ID, then the program will default to the following
            // page in the natural array sequence.
            nextPage: (state) => {
                if (
                    isSelected(
                        state,
                        CONST.PROMPT_FINANCES,
                        CONST.OPTION_FINANCES_BIZ_RENTAL,
                    )
                ) {
                    return CONST.PAGE_PRODUCTS;
                }
            },
        },
        {
            id: CONST.PAGE_GOALS,
            skippable: false,
            prompts: [
                CONST.PROMPT_GOALS
            ],
            nextPage: (state) => {
                if (
                    isSelected(
                        state,
                        CONST.PROMPT_FINANCES,
                        CONST.OPTION_FINANCES_COMPLEX
                    )
                ) {
                    return CONST.PAGE_FEATURES;
                }
            },
        },
        {
            id: CONST.PAGE_ACCESS,
            skippable: true,
            prompts: [
                CONST.PROMPT_ACCESS
            ],
            nextPage: (state) => {
                if (
                    isSelected(
                        state,
                        CONST.PROMPT_FINANCES,
                        CONST.OPTION_FINANCES_STRAIGHTFORWARD,
                    )
                ) {
                    if (
                        isSelected(
                            state,
                            CONST.PROMPT_ACCESS,
                            CONST.OPTION_ACCESS_OFFLINE,
                        )
                    ) {
                        return CONST.PAGE_PRODUCTS;
                    } else if (
                        isSelected(
                            state,
                            CONST.PROMPT_ACCESS,
                            CONST.OPTION_ACCESS_MOBILE,
                        ) ||
                        isSelected(
                            state,
                            CONST.PROMPT_ACCESS,
                            CONST.OPTION_ACCESS_WEB,
                        ) ||
                        isSkipped(
                            state,
                            CONST.PAGE_ACCESS,
                        )
                    ) {
                        return CONST.PAGE_PRODUCTS;
                    }
                } else if (
                    isSelected(
                        state,
                        CONST.PROMPT_FINANCES,
                        CONST.OPTION_FINANCES_COMPLEX,
                    )
                ) {
                    return CONST.PAGE_PRODUCTS;
                }
            },
        },
        {
            id: CONST.PAGE_FEATURES,
            skippable: true,
            prompts: [
                CONST.PROMPT_FEATURES
            ],
            nextPage: (state) => {
                if (
                    isSelected(
                        state,
                        CONST.PROMPT_FINANCES,
                        CONST.OPTION_FINANCES_COMPLEX
                    )
                ) {
                    if (
                        isSelected(
                            state,
                            CONST.PROMPT_FEATURES,
                        )
                    ) {
                        return CONST.PAGE_PRODUCTS;
                    } else if (
                        isSkipped(
                            state,
                            CONST.PAGE_FEATURES,
                        )
                    ) {
                        return CONST.PAGE_ACCESS;
                    }
                }
            },
        },
        {
            id: CONST.PAGE_PRODUCTS,
            skippable: false,
            prompts: [],
        },
    ],

    /**
     * Matches determine which product will be offered to the user
     * depending on their selections in the questionnaire.
     * They return boolean, true if this is a match.
     * They contain a list of products that are applicable to the match.
     */
    matches: [
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_STRAIGHTFORWARD
                ) && (
                    isSelected(
                        state,
                        CONST.PROMPT_ACCESS,
                        CONST.OPTION_ACCESS_MOBILE
                    ) ||
                    isSelected(
                        state,
                        CONST.PROMPT_ACCESS,
                        CONST.OPTION_ACCESS_WEB
                    )
                )
            ),
            products: [CONST.PRODUCT_SIMPLIFI],
        },
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_STRAIGHTFORWARD
                ) &&
                isSkipped(
                    state,
                    CONST.PAGE_ACCESS
                )
            ),
            products: [
                CONST.PRODUCT_SIMPLIFI,
                CONST.PRODUCT_DELUXE,
            ],
        },
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_COMPLEX
                ) &&
                isSkipped(
                    state,
                    CONST.PAGE_FEATURES
                ) &&
                isSkipped(
                    state,
                    CONST.PAGE_ACCESS
                )
            ),
            products: [
                CONST.PRODUCT_SIMPLIFI,
                CONST.PRODUCT_DELUXE,
            ],
        },
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_STRAIGHTFORWARD
                ) &&
                isSelected(
                    state,
                    CONST.PROMPT_ACCESS,
                    CONST.OPTION_ACCESS_OFFLINE
                )
            ),
            products: [
                CONST.PRODUCT_DELUXE
            ],
        },
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_COMPLEX
                ) &&
                isSelected(
                    state,
                    CONST.PROMPT_FEATURES
                )
            ),
            products: [
                CONST.PRODUCT_PREMIER
            ],
        },
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_COMPLEX
                ) &&
                isSkipped(
                    state,
                    CONST.PAGE_FEATURES
                ) &&
                isSelected(
                    state,
                    CONST.PROMPT_ACCESS,
                    CONST.OPTION_ACCESS_OFFLINE
                )
            ),
            products: [
                CONST.PRODUCT_DELUXE
            ],
        },
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_COMPLEX
                ) &&
                isSkipped(
                    state,
                    CONST.PAGE_FEATURES
                ) && (
                    isSelected(
                        state,
                        CONST.PROMPT_ACCESS,
                        CONST.OPTION_ACCESS_WEB,
                    ) ||
                    isSelected(
                        state,
                        CONST.PROMPT_ACCESS,
                        CONST.OPTION_ACCESS_MOBILE,
                    )
                )
            ),
            products: [
                CONST.PRODUCT_SIMPLIFI
            ],
        },
        {
            matcher: (state) => (
                isSelected(
                    state,
                    CONST.PROMPT_FINANCES, CONST.OPTION_FINANCES_BIZ_RENTAL
                )
            ),
            products: [
                CONST.PRODUCT_HOME_AND_BUSINESS
            ],
        },
    ],
};

export default program;